import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { RotinaDivergencia, RotinaGrupo } from '@/services/api/rotinas';
import { horaExibida, ordenarGrupos, traducaoDoGrupo } from './rotinaUtils';

type Item = { page_id: string; nome?: string | null; grupos?: string };

/**
 * Em quais grupos a página está, pela hora — o id do grupo não aparece em lugar
 * nenhum da tela. Quem está em todos vira "todos os N grupos".
 */
function descreverGrupos(ids: number[], ativos: RotinaGrupo[]): string {
    const deles = ativos.filter((g) => ids.includes(g.id));
    if (deles.length === ativos.length) {
        return ativos.length === 1 ? 'no único grupo' : `em todos os ${ativos.length} grupos`;
    }
    const horas = ordenarGrupos(deles).map(horaExibida).join(', ');
    return deles.length === 1 ? `no grupo das ${horas}` : `nos grupos das ${horas}`;
}

/**
 * Uma conferência pendente. O número fica no resumo e a lista inteira fica a um
 * clique. Quando todas as páginas estão nos mesmos grupos, isso é dito uma vez
 * só, no resumo, em vez de repetido em cada linha.
 */
function Conferencia({ tom, titulo, descricao, itens, acao }: {
    tom: 'amber' | 'red' | 'blue';
    titulo: string;
    descricao: string;
    itens: Item[];
    acao?: ReactNode;
}) {
    if (itens.length === 0) return null;
    const cores = {
        amber: 'border-amber-300 bg-amber-50 text-amber-900',
        red: 'border-destructive/40 bg-destructive/5 text-destructive',
        blue: 'border-sky-300 bg-sky-50 text-sky-900',
    }[tom];
    const gruposComuns = itens.every((i) => i.grupos && i.grupos === itens[0].grupos) ? itens[0].grupos : undefined;
    return (
        <details className={`rounded-lg border p-2.5 text-xs ${cores}`}>
            <summary className="cursor-pointer">
                <span className="font-semibold tabular-nums">{itens.length}</span>{' '}
                <span className="font-medium">{titulo}</span>
                <span className="mt-0.5 block font-normal opacity-80">
                    {descricao}
                    {gruposComuns && <> Estão {gruposComuns}.</>}
                </span>
            </summary>
            <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto border-t border-current/20 pt-2">
                {itens.map((i) => (
                    <li key={i.page_id}>
                        {/* Sem nome (página que saiu da conta) o link é o único jeito de saber qual é. */}
                        <a
                            href={`https://www.facebook.com/${i.page_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="underline-offset-2 hover:underline"
                            title="Abrir a página no Facebook"
                        >
                            {i.nome ? <span className="font-medium">{i.nome} </span> : null}
                            <span className="font-mono opacity-80">{i.page_id}</span> ↗
                        </a>
                        {i.grupos && !gruposComuns && <span className="block opacity-80">{i.grupos}</span>}
                    </li>
                ))}
            </ul>
            {acao && <div className="mt-2 border-t border-current/20 pt-2">{acao}</div>}
        </details>
    );
}

function Metrica({ rotulo, valor, dica }: { rotulo: string; valor: string; dica?: string }) {
    return (
        <div className="flex flex-col gap-0.5" title={dica}>
            <span className="text-xs text-muted-foreground">{rotulo}</span>
            <span className="text-xl font-bold tabular-nums">{valor}</span>
        </div>
    );
}

export default function PainelLateral({ grupos, divergencia, divergenciaComErro, onTirarDoMolde, tirando, somenteLeitura }: {
    grupos: RotinaGrupo[];
    divergencia: RotinaDivergencia | undefined;
    divergenciaComErro: boolean;
    onTirarDoMolde: (pageIds: string[]) => void;
    tirando: boolean;
    somenteLeitura?: boolean;
}) {
    const ativos = grupos.filter((g) => g.ativa);
    const paginasAtivas = ativos.reduce((s, g) => s + g.paginas.length, 0);
    const semTraducao = ativos.filter((g) => traducaoDoGrupo(g).tipo === 'sem');
    const paginasSemTraducao = semTraducao.reduce((s, g) => s + g.paginas.length, 0);

    const d = divergencia;
    const semDivergencia = d
        && d.na_rotina_pausadas.length === 0
        && d.na_rotina_nao_encontradas.length === 0
        && d.ativas_fora_da_rotina.length === 0;

    return (
        <aside className="flex flex-col gap-3">
            <div className="rounded-xl border bg-card p-4">
                <h3 className="mb-2 text-sm font-semibold">Antes de ligar</h3>
                <div className="space-y-2">
                    {semTraducao.length > 0 && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900">
                            <p>
                                <span className="font-semibold tabular-nums">{semTraducao.length}</span>{' '}
                                <span className="font-medium">grupo(s) sem tradução guardada</span>
                            </p>
                            <p className="mt-0.5 opacity-80">
                                {paginasSemTraducao} broadcast(s)/dia sairiam só no idioma-fonte — às{' '}
                                {semTraducao.map(horaExibida).join(', ')} (Brasilia).
                            </p>
                        </div>
                    )}

                    {/* Página pausada é assunto da tela de broadcast, e é lá que o
                        gestor resolve. Comentado, não apagado: a divergência continua
                        vindo da API e basta descomentar se a Rotina passar a agir nisso.
                    <Conferencia
                        tom="amber"
                        titulo="páginas pausadas"
                        descricao="Estão na Rotina e vão ser puladas, não recebem disparo."
                        itens={(d?.na_rotina_pausadas ?? []).map((p) => ({
                            page_id: p.page_id, nome: p.page_name, grupos: descreverGrupos(p.grupo_ids, ativos),
                        }))}
                    /> */}
                    <Conferencia
                        tom="red"
                        titulo="páginas que saíram da sua conta"
                        descricao={'Foram apagadas ou desconectadas e não aparecem mais entre as suas páginas, '
                            + 'então o nome se perdeu: clique no número para abrir no Facebook. Elas não disparam '
                            + 'e não travam nada, só ficam no molde.'}
                        itens={(d?.na_rotina_nao_encontradas ?? []).map((p) => ({
                            page_id: p.page_id, grupos: descreverGrupos(p.grupo_ids, ativos),
                        }))}
                        acao={somenteLeitura ? undefined :
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={tirando}
                                onClick={() => onTirarDoMolde((d?.na_rotina_nao_encontradas ?? []).map((p) => p.page_id))}
                            >
                                {tirando ? 'Tirando…' : 'Tirar do molde'}
                            </Button>
                        }
                    />
                    <Conferencia
                        tom="blue"
                        titulo="páginas ativas fora da Rotina"
                        descricao="São do gestor e não vão disparar neste dia."
                        itens={(d?.ativas_fora_da_rotina ?? []).map((p) => ({
                            page_id: p.page_id, nome: p.page_name,
                        }))}
                    />

                    {semDivergencia && semTraducao.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                            Nada pendente: sem divergência de páginas e todos os grupos têm tradução.
                        </p>
                    )}
                    {semDivergencia && semTraducao.length > 0 && (
                        <p className="text-xs text-muted-foreground">Sem divergência de páginas.</p>
                    )}
                    {divergenciaComErro && (
                        <p className="text-xs text-destructive">
                            Não foi possível conferir a divergência das páginas. Os números acima podem estar incompletos.
                        </p>
                    )}
                    {!d && !divergenciaComErro && (
                        <p className="text-xs text-muted-foreground">Conferindo as páginas…</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-xl border bg-card p-4">
                <Metrica
                    rotulo="Grupos ativos"
                    valor={`${ativos.length}/${grupos.length}`}
                    dica="Ativos de um total. O grupo desligado continua no molde e não dispara."
                />
                <Metrica
                    rotulo="Broadcasts/dia"
                    valor={paginasAtivas.toLocaleString('pt-BR')}
                    dica="Uma página = um broadcast. Soma das páginas dos grupos ativos, antes de pular as pausadas."
                />
                <Metrica
                    rotulo="1º disparo"
                    valor={ativos.length ? horaExibida(ativos[0]) : '—'}
                    dica="Horário de Brasília, como no calendário. No relógio da página a hora é outra."
                />
                <Metrica rotulo="Último" valor={ativos.length ? horaExibida(ativos[ativos.length - 1]) : '—'} />
            </div>
        </aside>
    );
}
